import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Table, InputNumber, Button, Radio, Checkbox, Select, Space, Tooltip, Tag, message } from 'antd';
import apiClient from '../../../shared/services/apiClient';

const MODES = [
    { value: 'grant', label: '赠送本期' },
    { value: 'permanent', label: '永久赠送' },
    { value: 'charge', label: '扣余额' },
];
const MODE_TIP = '赠送本期：现在免费，续费按新配置收；永久赠送：续费也不收；扣余额：同客户升级，按剩余天数折算';
const PLAN = ['cpu', 'memory', 'bandwidth'];

/** 后台云服务器升降配：CPU / 内存 / 带宽 / 系统盘 / 数据盘，计费规则同客户「升级配置」。 */
const AdminVmElasticModal = ({ open, vmId, onClose, onDone }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [targets, setTargets] = useState({});
    const [diskId, setDiskId] = useState(null);
    const [mode, setMode] = useState('grant');
    const [restart, setRestart] = useState(false);
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = (await apiClient.get(`/api/evm/admin/vms/${vmId}/elastic`)).data;
            if (r?.status === 'success') {
                setData(r.data);
                const t = {};
                (r.data.resources || []).forEach((x) => { t[x.resource] = x.current; });
                setTargets(t);
                const d = (r.data.resources || []).find((x) => x.resource === 'data_disk');
                setDiskId(d?.disks?.[0]?.id || null);
            } else message.error(r?.msg || '加载失败');
        } catch (e) {
            message.error(e.response?.data?.msg || e.message);
        } finally {
            setLoading(false);
        }
    }, [vmId]);

    useEffect(() => { if (open && vmId) load(); }, [open, vmId, load]);

    const apply = async (row) => {
        setBusy(row.resource);
        try {
            const r = (await apiClient.post(`/api/evm/admin/vms/${vmId}/elastic`, {
                resource: row.resource, target: targets[row.resource], mode, restart: restart ? 1 : 0,
                disk_id: row.resource === 'data_disk' ? diskId : undefined,
            })).data;
            if (r?.status === 'success') {
                const d = r.data || {};
                message.success(d.invoice_id ? (d.paid ? `已扣余额并生效（账单 #${d.invoice_id}）` : `余额不足，已生成待付账单 #${d.invoice_id}`) : '已生效');
                load();
                onDone && onDone();
            } else message.error(r?.msg || '调整失败');
        } catch (e) {
            message.error(e.response?.data?.msg || e.message);
        } finally {
            setBusy(null);
        }
    };

    const rows = (data?.resources || []).filter((x) => ['cpu', 'memory', 'bandwidth', 'sys_disk', 'data_disk'].includes(x.resource));
    const diskSize = (id) => (rows.find((x) => x.resource === 'data_disk')?.disks || []).find((d) => d.id === id)?.size;

    const columns = [
        { title: '配置', dataIndex: 'label', render: (l, r) => <Space size={4}>{l}{r.restart && <Tooltip title="重启后生效"><Tag>重启</Tag></Tooltip>}</Space> },
        { title: '当前', key: 'cur', render: (_, r) => `${r.resource === 'data_disk' ? (diskSize(diskId) ?? '-') : r.current} ${r.unit}` },
        {
            title: '调整为', key: 'target', render: (_, r) => (
                <Space>
                    {r.resource === 'data_disk' && (
                        <Select size="small" style={{ width: 90 }} value={diskId} onChange={(v) => { setDiskId(v); setTargets((t) => ({ ...t, data_disk: diskSize(v) })); }}
                            options={(r.disks || []).map((d) => ({ value: d.id, label: d.dev }))} />
                    )}
                    <InputNumber size="small" min={r.min || 0} max={r.max || undefined} step={r.step || 1} value={targets[r.resource]}
                        onChange={(v) => setTargets((t) => ({ ...t, [r.resource]: v }))} style={{ width: 100 }} />
                </Space>
            ),
        },
        {
            title: '', key: 'op', width: 80, render: (_, r) => (
                <Button size="small" type="primary" loading={busy === r.resource}
                    disabled={r.resource === 'data_disk' ? !diskId : targets[r.resource] === r.current}
                    onClick={() => apply(r)}>应用</Button>
            ),
        },
    ];

    return (
        <Modal title="升降配" open={open} onCancel={onClose} footer={null} width={640} destroyOnClose>
            <Space style={{ marginBottom: 12 }} wrap>
                <Tooltip title={MODE_TIP}><Radio.Group size="small" optionType="button" options={MODES} value={mode} onChange={(e) => setMode(e.target.value)} /></Tooltip>
                <Checkbox checked={restart} onChange={(e) => setRestart(e.target.checked)} disabled={!rows.some((x) => PLAN.includes(x.resource))}>立即重启</Checkbox>
                {data?.pending_restart && <Tag color="orange">待重启</Tag>}
            </Space>
            <Table rowKey="resource" size="small" loading={loading} columns={columns} dataSource={rows} pagination={false}
                locale={{ emptyText: '该节点未开启弹性配置' }} />
        </Modal>
    );
};

export default AdminVmElasticModal;
